<template>
  <default-layout class="guest-home">
    <main-container size="large">
      <guest-upgrade-prompt v-if="!$permissions.canHaveGuests" mode="hero" />
      <template v-else>
        <div v-if="$screen.isTabletOrMobile($mq)" class="people-home__header">
          <div>
            <h2>Guests</h2>
          </div>
          <div>
            <dd-button
              :class="AppSumoAssistant.getCreateAccountMembershipButtonMarkerClass()"
              color="blue" 
              size="small"
              new-icon
              @click="goToCreateGuests()"
            >
              Invite New Guest
            </dd-button>
          </div>
        </div>
        <div v-else :class="{'with-unregistered-users': hasMultipleUnregisteredUsers}" class="people-home__header">
          <div>
            <h2>Guests</h2>
            <sleek-filter-input  
              v-model="searchQuery" 
              class="filter-input" 
              placeholder="Filter by company, person, or email..."
            />
          </div>
          <div>
            <dd-button
              :class="AppSumoAssistant.getCreateAccountMembershipButtonMarkerClass()"
              type="flat"
              size="medium"
              new-icon
              @click="goToCreateGuestsCompany()"
            >Create Company</dd-button>
            <dd-button
              :class="AppSumoAssistant.getCreateAccountMembershipButtonMarkerClass()"
              color="blue"
              size="medium"
              new-icon
              @click="goToCreateGuests()"
            >Invite Guest</dd-button>
          </div>
          <!-- Bulk registration invitation -->
          <div 
            v-if="hasMultipleUnregisteredUsers && !bulkInvitationsAttempted" 
            class="unregistered-users-button" 
            @click="handleInviteUnregisteredUsers"
          >
            <span v-if="isSendingBulkInvitations">Sending...</span>
            <template v-else>
              <i class="ddi ddi-email" />
              <span>Send invite to {{ unregisteredUsersCount }} unregistered guests?</span>
            </template>
          </div>
        </div>
        <el-card class="people-home__card">
          <loading-placeholder v-if="isLoading" :lines="20" class="loading" />
          <template v-else>
            <div v-if="noFilterMatches" class="no-filter-matches">
              No filter matches
            </div>
            <!-- Company roster -->
            <div v-for="company in filteredCompanyRosters" :key="company.id" class="people-home__company-roster">
              <div class="header">
                <h3>{{ company.name }} <span class="number-of-people">{{company.numberOfPeople }}</span></h3>
                <more-button-dropdown v-if="!$screen.isMobile($mq)" class="more-button">
                  <template #options-list>
                    <ul>
                      <li @click="RouteAssistant.goToCompanyGuestsDetail($router, accountID, company.id)">
                        <i class="ddi ddi-edit"/>
                        <span>Edit Company</span>
                      </li>
                      <li v-if="noPeopleInCompany(company)" class="scary-list-option" @click="deleteCompany(company.id)">
                        <i class="ddi ddi-trash" />
                        <span>Delete Company</span>
                      </li>
                    </ul>
                  </template>
                </more-button-dropdown>
              </div>
              <guest-roster 
                :company="company" 
                @account-memberships-updated="fetchCompanyRosters"
                @person-deleted="fetchCompanyRosters"
                @account-role-update-disallowed="fetchCompanyRosters"
              />
            </div>
          </template>
        </el-card>
      </template>
    </main-container>
  </default-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import DefaultLayout from '@/layouts/default-layout.vue'
import MainContainer from '@/layouts/main-container.vue'
import { cloneDeep, debounce } from 'lodash-es'

/* Mixins */

/* Components */
import SleekFilterInput from '@/components/global/inputs/sleek-filter-input.vue'
import GuestRoster from './components/guest-roster.vue'
import GuestUpgradePrompt from '@/components/global/guest-upgrade-prompt.vue'

/* Helpers */

import RouteAssistant from '@/helpers/route-assistant'
import AppSumoAssistant from '@/helpers/appsumo-assistant'

/* Data types */
import { ICompanyRoster, IItemForSelection } from '@/api/types/response.types'

/* API */
import CompaniesAPI from '@/api/modules/companies.api'

export default defineComponent({
  name: 'GuestsHome',

  components: {
    DefaultLayout,
    MainContainer,
    SleekFilterInput,
    GuestRoster,
    GuestUpgradePrompt,
  },

  data() {
    return {
      AppSumoAssistant,
      isLoading: true,
      isSendingBulkInvitations: false,
      bulkInvitationsAttempted: false,
      unregisteredUsersCount: 0,
      debounceFilterCompanyRosters: null as any,
      RouteAssistant,
      searchQuery: '',
      companyRosters: [] as ICompanyRoster[],
      filteredCompanyRosters: [] as ICompanyRoster[],
    }
  },

  head: {
    title: 'Guests',
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    companies(): IItemForSelection[] {
      return this.companyRosters.map(c => ({
        id: c.id,
        name: c.name,
      }))
    },
    noFilterMatches(): boolean {
      // We will never have a case where an acct has no companies,
      // so if filtered companies length is 0, we know it's due to no filter matches.
      return this.filteredCompanyRosters.length === 0
    },
    hasSearchQuery(): boolean {
      return this.searchQuery.length > 0
    },
    hasMultipleUnregisteredUsers(): boolean {
      return this.unregisteredUsersCount > 1
    },
  },

  watch: {
    searchQuery: {
      immediate: false,
      handler() {
        this.debounceFilterCompanyRosters()
      },
    },
  },

  async created() {
    this.debounceFilterCompanyRosters = debounce(this.filterCompanyRosters, 300)
    this.isLoading = true
    await this.fetchCompanyRosters()
    this.isLoading = false
  },

  methods: {
    noPeopleInCompany(company: ICompanyRoster): boolean {
      return company.numberOfPeople === 0
    },
    filterCompanyRosters(): void {
      if (!this.hasSearchQuery) {
        this.filteredCompanyRosters = this.companyRosters
        return
      }
      this.filteredCompanyRosters = []
      this.companyRosters.forEach(company => {
        // Company match...
        if (
          company.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        ) {
          this.filteredCompanyRosters.push(company)
        } else {
          // People match...
          const peopleMatches = company.listPeople.filter(person => {
            return (
              person.name
                .toLowerCase()
                .includes(this.searchQuery.toLowerCase()) ||
              person.email
                .toLowerCase()
                .includes(this.searchQuery.toLowerCase())
            )
          })

          const curCompany: ICompanyRoster = cloneDeep(company)
          curCompany.listPeople = peopleMatches

          if (peopleMatches.length > 0) {
            this.filteredCompanyRosters.push(curCompany)
          }
        }
      })
    },
    async goToCreateGuestsCompany() {
      if (
        !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
          this.$router,
          this.$permissions,
        ))
      ) {
        return
      }

      RouteAssistant.goToCreateGuestsCompany(this.$router, this.accountID)
    },
    async goToCreateGuests() {
      if (
        !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
          this.$router,
          this.$permissions,
        ))
      ) {
        return
      }

      RouteAssistant.goToCreateGuests(this.$router, this.accountID)
    },
    async fetchCompanyRosters() {
      try {
        this.companyRosters = await CompaniesAPI.get_GuestsRosters()
        this.unregisteredUsersCount = this.companyRosters
          .map(company => company.listPeople)
          .reduce((acc, people) => acc.concat(people), [])
          .filter(person => !person.hasRegistered).length

        this.filterCompanyRosters()
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async deleteCompany(id: number) {
      try {
        const response = await CompaniesAPI.delete_Delete(id)
        this.$messaging.showSuccess(response.message)
        this.isLoading = true
        await this.fetchCompanyRosters()
        this.isLoading = false
      } catch (error) {
        this.$error.emit(error)
      }
    },
    handleInviteUnregisteredUsers() {
      this.$confirm(
        `They'll receive an email with a link and instructions to complete their registration.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Send invites',
          confirmButtonClass: 'el-button--primary',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Send invites to ${
            this.unregisteredUsersCount
          } unregistered guests?`,
        },
      ).then(() => {
        this.inviteUnregisteredUsers()
      })
    },
    async inviteUnregisteredUsers() {
      try {
        this.isSendingBulkInvitations = true
        const response = await CompaniesAPI.get_BulkGuestsInvitation()
        this.$messaging.showSuccess(response.message)
        this.isSendingBulkInvitations = false
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.bulkInvitationsAttempted = true
      }
    },
  },
})
</script>

<style lang="scss">
.people-home {
  &__header {
    position: relative;
    display: flex;
    justify-content: space-between;
    padding-bottom: 14px;

    &.with-unregistered-users {
      padding-bottom: 30px;
    }

    @include media-breakpoint-down(sm) {
      margin-left: 20px;
      margin-right: 20px;
    }

    > div {
      display: flex;
      align-items: center;

      > :not(:last-child) {
        margin-right: 10px;
      }

      .filter-input {
        margin-left: 10px !important;
      }
    }

    .unregistered-users-button {
      position: absolute;
      left: 50%;
      bottom: -16px;
      transform: translateX(-50%);
      border: $border--solid-light;
      border-radius: 20px;
      background-color: $color--gray-75;
      padding: 4px 20px;
      width: fit-content;
      @include lettering--p-sm;
      color: $color--white;
      font-weight: $bold;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      @include flex-row-horizontal-spacing(4px);

      &:hover {
        background-color: $color--gray-90;
      }
    }
  }

  &__card {
    overflow: visible;
    padding: 0 20px 40px 20px;

    @include media-breakpoint-down(sm) {
      padding-left: 5px;
      padding-right: 5px;

      &.el-card {
        border-radius: 0 !important;
      }
    }

    .loading {
      padding-top: 20px;
    }

    .no-filter-matches {
      padding-top: 50px;
      display: flex;
      justify-content: center;
      color: var(--color-text-third);
    }
  }

  &__company-roster {
    &:not(:first-child) {
      padding-top: 30px;
    }

    .header {
      display: flex;
      align-items: center;
      border-bottom: solid 2px $color--gray-15;
      width: calc(100% + 72px);
      left: -36px;
      position: relative;
      padding: 2px 36px 14px 36px;

      .more-button {
        margin-left: 10px;
      }

      @include media-breakpoint-down(md) {
        padding-bottom: 12px;
      }

      > h3 {
        margin: 0;
      }

      .number-of-people {
        padding-left: 6px;
        @include lettering--p;
        color: var(--color-text-third);
      }
    }
  }
}
</style>
